import React, { useState, useEffect } from 'react';
import {
    Button,
    Col,
    Drawer,
    Form,
    Input,
    Row,
    Select,
    Space,
    message,
    DatePicker,
    InputNumber,
    Spin,
    Typography,
    Modal
} from 'antd';
import { 
    CloseOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { insert, getById, update } from '../../../../../apis/CreditoFonacotApi';
import { getList as getTiposSaldo } from '../../../../../apis/TipoSaldoApi';
import { getList as getPeriodos } from '../../../../../apis/PeriodosApi';
import { getEmpleados } from '../../../../../apis/OdooApi';
import dayjs from 'dayjs';

const { Option } = Select;
const { Text } = Typography;

const App = ({ title, empleadoId, id, open, finish }) => {
    const [form] = Form.useForm();
    const [ID, setID] = useState(id);
    const [modified, setModified] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [tiposSaldo, setTiposSaldo] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [periodos, setPeriodos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingEmpleados, setLoadingEmpleados] = useState(false);
    const [loadingPeriodos, setLoadingPeriodos] = useState(false);
    const [descuentoPeriodo, setDescuentoPeriodo] = useState(null);
    const [defaultTipoSaldoId, setDefaultTipoSaldoId] = useState(null);

    const calcularDescuentoPorPeriodo = (descuentoMensual, periodoId) => {
        if (!descuentoMensual || !periodoId) return null;
        
        const periodo = periodos.find(p => p.value === periodoId);
        if (!periodo) return null;

        // Lógica para calcular el descuento según el período
        // Ejemplo: si es quincenal, dividir entre 2
        const factor = periodo.text.toLowerCase().includes('quincenal') ? 2 : 
                      periodo.text.toLowerCase().includes('semanal') ? 4 : 1;
        
        return descuentoMensual / factor;
    };

    const handlePeriodoChange = (periodoId) => {
        const descuentoMensual = form.getFieldValue('descuento_mensual');
        if (descuentoMensual && periodoId) {
            const descuento = calcularDescuentoPorPeriodo(descuentoMensual, periodoId);
            setDescuentoPeriodo(descuento);
        } else {
            setDescuentoPeriodo(null);
        }
    };

    const handleDescuentoMensualChange = (value) => {
        const periodoId = form.getFieldValue('periodo_id');
        if (value && periodoId) {
            const descuento = calcularDescuentoPorPeriodo(value, periodoId);
            setDescuentoPeriodo(descuento);
        } else {
            setDescuentoPeriodo(null);
        }
    };

    const handleMontoInicialChange = (value) => {
        // Sincronizar saldo con monto_inicial
        form.setFieldsValue({ saldo: value });
        // Calcular descuento mensual automáticamente
        calcularDescuentoMensual(value, form.getFieldValue('plazo'));
    };

    const handlePlazoChange = (value) => {
        // Calcular descuento mensual automáticamente
        calcularDescuentoMensual(form.getFieldValue('monto_inicial'), value);
    };

    const calcularDescuentoMensual = (montoInicial, plazo) => {
        if (montoInicial && plazo && plazo > 0) {
            const descuentoCalculado = montoInicial / plazo;
            form.setFieldsValue({ descuento_mensual: Math.round(descuentoCalculado * 100) / 100 });
        }
    };

    const calcularPlazo = (montoInicial, descuentoMensual) => {
        if (montoInicial && descuentoMensual && descuentoMensual > 0) {
            const plazoCalculado = Math.ceil(montoInicial / descuentoMensual);
            form.setFieldsValue({ plazo: plazoCalculado });
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (!values) return;

            // Modal de confirmación
            Modal.confirm({
                title: 'Confirmar guardado',
                content: (
                    <div style={{ marginTop: '20px' }}>
                        <p><strong>¿Deseas guardar este crédito FONACOT?</strong></p>
                        <p style={{ marginBottom: '5px', marginTop: '15px' }}>
                            <strong>Monto Inicial:</strong> ${values.monto_inicial?.toFixed(2) || '0.00'}
                        </p>
                        <p style={{ marginBottom: '5px' }}>
                            <strong>Plazo:</strong> {values.plazo || 0} meses
                        </p>
                        <p style={{ marginBottom: '5px' }}>
                            <strong>Descuento Mensual:</strong> ${values.descuento_mensual?.toFixed(2) || '0.00'}
                        </p>
                    </div>
                ),
                okText: 'Guardar',
                cancelText: 'Cancelar',
                onOk: async () => {
                    setLoading(true);
                    const payload = {
                        empleado_id: values.empleado_id,
                        tipo_saldo: values.tipo_saldo || (defaultTipoSaldoId ? String(defaultTipoSaldoId) : undefined),
                        fecha_inicio: dayjs(values.fecha_inicio).format('YYYY-MM-DD'),
                        fecha_suspension: values.fecha_suspension ? dayjs(values.fecha_suspension).format('YYYY-MM-DD') : null,
                        monto_inicial: values.monto_inicial || 0,
                        descuento_mensual: values.descuento_mensual,
                        plazo: values.plazo,
                        numero_credito: values.numero_credito,
                        saldo: values.saldo || values.monto_inicial || 0
                    };

                    if (ID > 0) {
                        const response = await update(ID, payload);
                        if(response.ok){
                            message.success('Crédito Fonacot actualizado correctamente');
                            setModified(true);
                            onClose(true);
                        }
                    } else {
                        const response = await insert(payload);
                        if(response.ok){
                            setID(response.payload);
                            message.success('Crédito Fonacot creado correctamente');
                            setModified(true);
                            onClose(true);
                        }
                    }
                    setLoading(false);
                },
                onCancel() {
                    setLoading(false);
                }
            });
        } catch (errorInfo) {
            console.log('Failed:', errorInfo);
            setLoading(false);
        }
    };

    const onClose = (reload) => {
        finish(reload ?? modified);
        setInitialValues(null);
        setDescuentoPeriodo(null);
        form.resetFields();
    };

    useEffect(() => {
        if (open) {
            (async () => {
                setLoading(true);
                try {
                    const [tipoSaldoIdCFO] = await Promise.all([
                        getTiposSaldos(),
                        getEmpleadosOdoo(),
                        getPeriodosList()
                    ]);

                    if (ID > 0) {
                        await refreshData(tipoSaldoIdCFO);
                    } else {
                        const defaultValues = {
                            fecha_inicio: dayjs(),
                            monto_inicial: 0,
                            saldo: 0,
                            plazo: 0,
                            descuento_mensual: 0,
                            empleado_id: empleadoId || null,
                            tipo_saldo: tipoSaldoIdCFO ? String(tipoSaldoIdCFO) : undefined
                        };
                        setInitialValues(defaultValues);
                        form.setFieldsValue(defaultValues);
                    }
                    setModified(false);
                } catch (error) {
                    console.error('Error loading initial data:', error);
                    message.error('Error al cargar datos iniciales');
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [open, ID]);

    const refreshData = async (tipoSaldoIdCFO = null) => {
        const response = await getById(ID);
        if(response.ok){
            let item = response.payload;
            const formValues = {
                ...item,
                fecha_inicio: dayjs(item.fecha_inicio),
                fecha_suspension: item.fecha_suspension ? dayjs(item.fecha_suspension) : null,
                empleado_id: item.empleado_id,
                tipo_saldo: tipoSaldoIdCFO ? String(tipoSaldoIdCFO) : (defaultTipoSaldoId ? String(defaultTipoSaldoId) : (item.tipo_saldo ? String(item.tipo_saldo) : undefined))
            };
            setInitialValues(formValues);
            form.setFieldsValue(formValues);
        }
    };

    const getTiposSaldos = async () => {
        try {
            const response = await getTiposSaldo();
            if(response.ok) {
                const items = response.payload || [];
                setTiposSaldo(items);

                const tipoSaldoCFO = items.find(
                    (item) => item?.clave === 'CFO' || item?.text === 'CFO'
                );

                if (tipoSaldoCFO?.value) {
                    setDefaultTipoSaldoId(tipoSaldoCFO.value);
                    return tipoSaldoCFO.value;
                }

                message.error('No se encontró Tipo de Saldo con clave CFO');
                return null;
            }
            return null;
        } catch (error) {
            console.error('Error obteniendo tipos de saldo:', error);
            message.error('Error al cargar tipos de saldo');
            return null;
        }
    };

    const getEmpleadosOdoo = async () => {
        try {
            setLoadingEmpleados(true);
            const response = await getEmpleados();
            if(response.ok) {
                setEmpleados(response.payload);
            }
        } catch (error) {
            console.error('Error obteniendo empleados:', error);
            message.error('Error al cargar lista de empleados');
        } finally {
            setLoadingEmpleados(false);
        }
    };

    const getPeriodosList = async () => {
        try {
            setLoadingPeriodos(true);
            const response = await getPeriodos();
            if(response.ok) {
                setPeriodos(response.payload);
            }
        } catch (error) {
            console.error('Error obteniendo períodos:', error);
            message.error('Error al cargar lista de períodos');
        } finally {
            setLoadingPeriodos(false);
        }
    };

    if (!initialValues) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            </div>
        );
    }

    return (
        <Drawer
            title={`${title} ${ID > 0 ? `#${ID}` : ''}`}
            width={720}
            placement="right"
            onClose={onClose}
            open={open}
            closable={true}
            extra={
                <Space>
                    <Button onClick={onClose} icon={<CloseOutlined />}>Cancelar</Button>
                    <Button 
                        onClick={handleOk} 
                        type="primary"
                        loading={loading}
                    >
                        Guardar
                    </Button>
                </Space>
            }
        >
            <Form
                layout="vertical"
                form={form}
            >
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="empleado_id"
                            label="Empleado"
                            rules={[{ required: true, message: 'Seleccione un empleado' }]}
                        >
                            <Select
                                placeholder="Seleccione un empleado"
                                loading={loadingEmpleados}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {empleados.map((item) => (
                                    <Option key={item.value} value={item.value}>
                                        {item.text}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={24} xs={24}>
                        <Form.Item
                            name="tipo_saldo"
                            label="Tipo de Saldo"
                            rules={[{ required: true, message: 'Seleccione el tipo de saldo' }]}
                        >
                            <Select placeholder="Seleccione tipo de saldo" disabled>
                                {tiposSaldo.map((item) => (
                                    <Option key={item.value} value={String(item.value)}>
                                        {item.displayTitle}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={24} xs={24}>
                        <Form.Item
                            name="numero_credito"
                            label="Número de Crédito"
                            rules={[
                                { required: true, message: 'Ingrese el número de crédito' },
                                { pattern: /^[A-Za-z0-9]+$/, message: 'Solo caracteres alfanuméricos' }
                            ]}
                        >
                            <Input 
                                placeholder="Número único de crédito" 
                                maxLength={20}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={24} xs={24}>
                        <Form.Item
                            name="fecha_inicio"
                            label="Fecha de Inicio"
                            rules={[{ required: true, message: 'Seleccione fecha de inicio' }]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={24} xs={24}>
                        <Form.Item
                            name="fecha_suspension"
                            label="Fecha de Suspensión"
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                allowClear
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={24} xs={24}>
                        <Form.Item
                            name="monto_inicial"
                            label="Monto Inicial"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={100}
                                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                onChange={handleMontoInicialChange}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={24} xs={24}>
                        <Form.Item
                            name="descuento_mensual"
                            label="Descuento Mensual"
                            rules={[{ required: true, message: 'Ingrese el descuento mensual' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={100}
                                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                onChange={handleDescuentoMensualChange}
                            />
                        </Form.Item>
                    </Col>

                    {descuentoPeriodo !== null && (
                        <Col span={24}>
                            <Text strong>
                                Descuento por período: ${descuentoPeriodo.toFixed(2)}
                            </Text>
                        </Col>
                    )}

                    <Col md={12} sm={24} xs={24}>
                        <Form.Item
                            name="plazo"
                            label="Plazo (meses)"
                            rules={[{ required: true, message: 'Ingrese el plazo en meses' }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                max={120}
                                onChange={handlePlazoChange}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} sm={24} xs={24}>
                        <Form.Item
                            name="saldo"
                            label="Saldo Actual"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                disabled
                                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
};

export default App;